
import React, { useState, useEffect } from 'react';

import RegisteredEmailDomainInput, {
  FIELDS,
} from './RegisteredEmailDomainInput';
import { RegisteredTypes } from '../../../service/emailDomainService';
import { testEmailAgainstRegex } from '../../../lib/airframe/FormValidation';
import { MessageTypes, type Message } from '../styledComponent/MessageDisplay';
import { caseInsensitiveIncludes } from '../../../lib/array';

interface RegisteredEmailDomainInputContainerProps {
  id: string;
  email: string;
  setEmail: (email: string) => void;
  setDisplayMessage: (message?: Message) => void;
  displayMessage?: Message;
  domainList: string[];
  emailList: string[];
}

export const getEmailParts = (email: string) => {
  const splitEmail = email.split('@');
  const splitEmailLastIndex = splitEmail.length - 1;

  const username = splitEmail.slice(0, splitEmailLastIndex).join('@'),
    domain = splitEmail[splitEmailLastIndex];
  return { username, domain };
};

export const testUsernameAgainstRegex = (username: string): boolean => {
  const re = /^[a-zA-Z0-9_\-.]+$/;
  return re.test(username);
};

const contactSupportMessage =
  'Please contact helpdesk@cirium.com to add registered email to your account.';

  const RegisteredEmailDomainInputContainer: React.FC<RegisteredEmailDomainInputContainerProps> = ({
    id,
    email,
    setEmail,
    setDisplayMessage,
    displayMessage,
    domainList,
    emailList,
  }) => {
  const [registeredTypeValue, setRegisteredTypeValue] = useState(
    RegisteredTypes.DOMAIN,
  );

  const hasInvalidDomainEmail = () => {
    let hasError = false;
    const { username, domain } = getEmailParts(email);
    // Validate username
    if (!testUsernameAgainstRegex(username)) {
      hasError = true;
      setDisplayMessage({
        field: FIELDS.USERNAME,
        type: MessageTypes.ERROR,
        message: username
          ? 'Username can only have alphanumerics, dots, underscores and hyphens.'
          : 'Please enter a valid email username',
      });
    }

    // Validate domain
    if (domainList?.length) {
      if (!domainList.includes(domain.toLowerCase())) {
        hasError = true;
        setDisplayMessage({
          field: FIELDS.DOMAIN,
          type: MessageTypes.ERROR,
          message: 'Please select valid registered domain.',
        });
      }
    } else {
      hasError = true;
      setDisplayMessage({
        type: MessageTypes.ERROR,
        message: contactSupportMessage,
      });
    }
    return hasError;
  };

  const hasInvalidEmail = () => {
    let hasError = false;
    if (emailList?.length) {
      if (!caseInsensitiveIncludes(emailList, email)) {
        hasError = true;
        setDisplayMessage({
          field: FIELDS.EMAIL,
          type: MessageTypes.ERROR,
          message: 'Please select a valid registered email address.',
        });
      }
    } else {
      hasError = true;
      setDisplayMessage({
        type: MessageTypes.ERROR,
        message: contactSupportMessage,
      });
    }
    return hasError;
  };

  // Main email validation
  useEffect(() => {
    // this is a local flag b/c react setState isn't synchronous, so we can't just check "!!displayMessage"
    let hasNewError = false;
    setDisplayMessage();
    console.log(registeredTypeValue);
    // if set to the email-address type input
    if (registeredTypeValue === RegisteredTypes.EMAIL_ADDRESS) {
      hasNewError = hasInvalidEmail();
      // if set to the domain type input
    } else if (registeredTypeValue === RegisteredTypes.DOMAIN) {
      hasNewError = hasInvalidDomainEmail();
    }

    // if there were no business-rule based errors... just check if the email itself is valid
    if (!hasNewError && email && !testEmailAgainstRegex(email)) {
      setDisplayMessage({
        type: MessageTypes.ERROR,
        message: 'Please enter a valid email address.',
      });
    }
  }, [email, registeredTypeValue, domainList && emailList]);

  useEffect(() => {
    if (
      registeredTypeValue === RegisteredTypes.DOMAIN &&
      hasInvalidDomainEmail() &&
      emailList.length &&
      !hasInvalidEmail() &&
      testEmailAgainstRegex(email)
    ) {
      setRegisteredTypeValue(RegisteredTypes.EMAIL_ADDRESS);
    }
  }, [domainList && emailList]);

  return (
    <RegisteredEmailDomainInput
      id={id}
      email={email}
      setEmail={setEmail}
      domainList={domainList}
      emailList={emailList}
      registeredTypeValue={registeredTypeValue}
      setRegisteredTypeValue={setRegisteredTypeValue}
      displayMessage={displayMessage}
    />
  );
};

export default RegisteredEmailDomainInputContainer;
